/* ============================================================================
   nt-measure.js — SHARED MEASUREMENT / QUOTING MODULE (task #50)

   The ONE place artwork dimensions are measured for quoting. Every engine
   (bySize_popular, pdp_transferBySize, bysize-v4, __blanksUploadHelper) is
   converging on this module; do not add a private dimension path to an
   engine again — that drift is how four different spellings of one constant
   (4.16 / 4.167 / 4.1666… / 4.168…) and the v4 vector 4.167× oversizing
   happened.

   ⚠ CONTRACT — QUOTING ONLY. This module returns NUMBERS, never URLs.
   NEVER use it (or its internal render URLs) to build an order, cart, or
   production URL. Quoting cannot damage a print file; asset selection can —
   they are separate modules by design (Owen 2026-07-28). The one-URL-two-
   purposes mistake caused the cart-zoom bug (PR #223) and the PixelCut
   w=2000 production leak (PR #230).

   THE UNIT RULE (verified 2026-07-28 against purchased variant_title
   history — the size customers actually bought):

       trueInches = trimmedRenderPx / (vector 72 | raster 300)

   imgix rasterises VECTOR artwork in PostScript POINTS (1pt = 1/72in), so a
   vector render's pixel size IS its true size at 72dpi. Raster sources are
   already pixels, read against the 300dpi print basis. pdf/psd go to
   calculateInches in s3Key mode, which returns TRUE inches for those types
   (verified with a hand-built exactly-10×10in PDF → 10.00 × 10.00).

   ⚠ Do NOT reintroduce calculateInches for eps/ai and do NOT re-add a
   ×4.16* constant: calculateInches returns DIFFERENT UNITS per call mode
   (?url= is a 72dpi render ÷300; ?s3Key= is inconsistent per stored object)
   and its dpi field cannot disambiguate.

   ⚠ Measurement renders must NEVER carry w=/h= (a kept w=2000 is why every
   wide file quoted exactly 6.67") and never bg-remove without the bbox
   method below (imgix DOWNSCALES any bg-remove render that has no explicit
   w — 6144px arrived as 3814px — so its pixel count is not source pixels).

   BG-REMOVED SIZING (content-fraction / mask-bbox method): the customer is
   charged for the art that remains after background removal, but no single
   render both preserves the source pixel basis and shows the removal result.
   So: measure the UNTRIMMED bg-removed render at a known width (≤ source, so
   nothing upscales; no trim, so frame→source mapping stays linear), scan its
   alpha bounding box on canvas, and rescale:

       contentSourcePx = bboxRenderPx × (sourceFramePx / renderFramePx)

   ~1.5% accuracy from bbox quantisation at reduced scale. Do NOT "simplify"
   to pinning w=<source width> on a trimmed render — trim crops the frame
   FIRST, so w then upscales the trimmed region and inflates the quote
   (measured: 1.73" → 3.00"). The canvas scan runs on a ≤1600px render, never
   the full-res source (getImageData at 6600² ≈ 174MB = mobile crash).
   ========================================================================= */
(function () {
  'use strict';
  if (window.NTMeasure) return;

  var API_URL = 'https://k7ylxp3inc.execute-api.eu-west-2.amazonaws.com/production';
  var PRINT_DPI = 300;
  var RENDER_DPI = { vector: 72, raster: 300 };
  // pdf joined the vector path 2026-08-03: calculateInches returns the PDF's
  // PAGE BOX, not the art — a wide logo on a letter page quoted 8.5x11 while
  // every other vector type quotes the TRIMMED extent. imgix rasterises PDF at
  // exact 72dpi points (verified: unimetal-vec letter page rendered 612x792 =
  // 8.5x11.00 to the pixel; trimmed art 466x106pt = 6.47x1.47). Only psd stays
  // on the service — imgix cannot reliably rasterise it.
  var VECTOR_EXTS = ['eps', 'ai', 'svg', 'pdf'];
  var SERVICE_EXTS = ['psd'];
  // Matches bg-removal-multi.js ALPHA_TRIM_THRESHOLD — semi-transparent halo
  // pixels below this don't count as content.
  var ALPHA_THRESHOLD = 16;
  var BBOX_RENDER_MAX_W = 1600;

  // Source frame sizes, keyed by bare imgix URL. Frames never change for a
  // stored upload, so this never invalidates.
  var frameCache = {};

  function loadImage(url, needPixels) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      if (needPixels) img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('nt-measure: image failed to load: ' + url));
      };
      img.src = url;
    });
  }

  // Alpha bounding box of a decoded render, in that render's pixels.
  // Returns null when the canvas is tainted (CORS) or nothing is opaque —
  // callers fall back to the trim-only measure.
  function scanAlphaBBox(img) {
    var w = img.naturalWidth;
    var h = img.naturalHeight;
    if (!w || !h) return null;
    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null;
    }
    var minX = w, maxX = -1, minY = h, maxY = -1;
    for (var y = 0; y < h; y++) {
      var rowStart = y * w * 4;
      for (var x = 0; x < w; x++) {
        if (data[rowStart + x * 4 + 3] > ALPHA_THRESHOLD) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { w: maxX - minX + 1, h: maxY - minY + 1, frameW: w, frameH: h };
  }

  function serviceInches(s3Key) {
    return fetch(API_URL + '/calculateInches?s3Key=' + encodeURI(s3Key))
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.success === true && res.data && res.data.dimensions &&
            res.data.dimensions.width && res.data.dimensions.height) {
          return { inW: res.data.dimensions.width, inH: res.data.dimensions.height };
        }
        return null;
      })
      .catch(function () { return null; });
  }

  function sourceFrame(imgixUrl) {
    if (frameCache[imgixUrl]) return Promise.resolve(frameCache[imgixUrl]);
    return loadImage(imgixUrl).then(function (img) {
      var f = { w: img.naturalWidth, h: img.naturalHeight };
      frameCache[imgixUrl] = f;
      return f;
    });
  }

  function fromInches(inW, inH, method) {
    return {
      pxW: Math.round(inW * PRINT_DPI),
      pxH: Math.round(inH * PRINT_DPI),
      inW: inW,
      inH: inH,
      method: method
    };
  }

  // Trim-only render at native size — the default measure for anything the
  // client can decode. No w/h, no bg-remove, ever (see header). fm=png is
  // pinned for vectors only: without it imgix serves a JPEG render of an
  // .ai/.eps (same dimensions, but pin the proven v4 measure params); for
  // rasters the output format never changes the dimensions, and skipping
  // fm=png keeps a big JPG's measure render at JPG weight.
  function trimMeasure(imgixUrl, dpi, method) {
    var params = dpi === RENDER_DPI.vector
      ? '?trim=colorUnlessAlpha&fm=png'
      : '?trim=colorUnlessAlpha';
    return loadImage(imgixUrl + params).then(function (img) {
      if (!img.naturalWidth || !img.naturalHeight) return null;
      return fromInches(img.naturalWidth / dpi, img.naturalHeight / dpi, method);
    });
  }

  // Content-fraction measure of a bg-removed raster (see header). Any failure
  // (CORS taint, empty bbox, load error) returns null and the caller falls
  // back to the pre-removal trimmed size — conservative, never garbage.
  // modelParam (optional): the imgix bg-remove-model of the render the
  // CUSTOMER CHOSE (v4 3-option picker; null/undefined = imgix default).
  // Without it the quote measured the DEFAULT model regardless of choice —
  // full-frame residue on some sources quoted the whole source frame
  // (US fix 2026-08-11, ported).
  function bgRemovedMeasure(imgixUrl, modelParam, bgRemovedUrl) {
    return sourceFrame(imgixUrl).then(function (frame) {
      if (!frame.w || !frame.h) return null;
      var renderW = Math.min(BBOX_RENDER_MAX_W, frame.w);
      var url = imgixUrl + '?bg-remove=true' +
        (modelParam ? '&bg-remove-model=' + modelParam : '') +
        '&fm=png&w=' + renderW;
      // Hosted removal results may have been resized by the AI pipeline.
      // Measure their untrimmed frame and map the content back to the source.
      if (bgRemovedUrl) {
        var hosted = new URL(bgRemovedUrl);
        ['w', 'h', 'trim', 'trim-tol', 'rect', 'fit', 'crop'].forEach(function (key) {
          hosted.searchParams.delete(key);
        });
        hosted.searchParams.set('w', renderW);
        hosted.searchParams.set('fm', 'png');
        url = hosted.toString();
      }
      return loadImage(url, true).then(function (img) {
        var bbox = scanAlphaBBox(img);
        if (!bbox) return null;
        var contentSrcW = bbox.w * (frame.w / bbox.frameW);
        var contentSrcH = bbox.h * (frame.h / bbox.frameH);
        return fromInches(
          contentSrcW / RENDER_DPI.raster,
          contentSrcH / RENDER_DPI.raster,
          'raster-bgr-bbox'
        );
      });
    });
  }

  /**
   * Measure an upload for QUOTING.
   *
   * @param {Object} opts
   * @param {string} opts.imgixUrl  bare imgix URL of the upload (no params)
   * @param {string} opts.ext      file extension, any case
   * @param {string} [opts.s3Key]   stored object key / upload file name —
   *                                required for pdf/psd (service mode)
   * @param {boolean} [opts.bgRemove] quote the post-background-removal extent
   *                                (rasters only; vectors ship untouched and
   *                                never re-quote for BGR)
   * @param {string} [opts.bgRemovedUrl] untrimmed hosted removal result, if any
   * @returns {Promise<{pxW,pxH,inW,inH,method}|null>} px on the 300dpi print
   *          basis, inches as numbers. null = measurement failed; callers
   *          keep their own fallback (do NOT invent one here).
   */
  function measure(opts) {
    var ext = String(opts.ext || '').toLowerCase();
    var run;

    if (SERVICE_EXTS.indexOf(ext) !== -1) {
      // psd ONLY — and the sole exception to the post-trim rule (Owen
      // 2026-08-03: "size needs to be calculated post trim" for everything),
      // because imgix cannot rasterise psd for a client-side trim measure.
      // The service reports the psd's canvas, not the trimmed art.
      run = serviceInches(opts.s3Key).then(function (r) {
        return r ? fromInches(r.inW, r.inH, 'service-s3key') : null;
      });
    } else if (VECTOR_EXTS.indexOf(ext) !== -1) {
      run = trimMeasure(opts.imgixUrl, RENDER_DPI.vector, 'vector-72');
    } else if (opts.bgRemove) {
      // .catch BEFORE the fallback: a rejected bg render (load failure) must
      // still fall back to the pre-removal trimmed size, same as a null bbox.
      run = bgRemovedMeasure(opts.imgixUrl, opts.bgRemoveModel, opts.bgRemovedUrl)
        .catch(function () { return null; })
        .then(function (r) {
          return r || trimMeasure(opts.imgixUrl, RENDER_DPI.raster, 'raster-trim-fallback');
        });
    } else {
      run = trimMeasure(opts.imgixUrl, RENDER_DPI.raster, 'raster-trim');
    }

    return run.catch(function (err) {
      console.log('ERROR NTMeasure.measure', err && err.message);
      return null;
    });
  }

  window.NTMeasure = Object.freeze({ measure: measure });
})();
